import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

/**
 * PostComments — flat list of comments on a post, grouped into TWO tiers
 * (top-level comment → replies). Backend caps replies-of-replies, so this
 * component only needs to render depth 1.
 *
 * Props:
 *   postId         the post whose comments we're loading
 *   postOwnerId    user_id of the post author (allowed to delete any comment)
 *   currentUserId  the viewer (allowed to delete their own comments)
 *   onCountChange  optional callback with the new live comment count after
 *                  create/delete so the parent card can update its pill
 */
const MAX = 500;

export default function PostComments({ postId, postOwnerId, currentUserId, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);   // top-level comment id
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const taRef = useRef(null);

  const liveCount = useCallback((rows) => {
    // Only non-deleted comments count toward the live pill.
    return rows.filter(r => !r.deleted).length;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/commons/posts/${postId}/comments`);
      const rows = r.data?.comments || [];
      setComments(rows);
      onCountChange?.(liveCount(rows));
    } catch (e) {
      console.error("[comments] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [postId, onCountChange, liveCount]);

  useEffect(() => { load(); }, [load]);

  async function send() {
    const body = text.trim().slice(0, MAX);
    if (!body || posting) return;
    setPosting(true);
    try {
      const r = await api.post(`/commons/posts/${postId}/comments`, {
        body,
        parent_comment_id: replyingTo || null,
      });
      const next = [...comments, r.data];
      setComments(next);
      setText("");
      setReplyingTo(null);
      onCountChange?.(liveCount(next));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not post comment");
    } finally {
      setPosting(false);
    }
  }

  async function remove(c) {
    if (!window.confirm("delete this comment?")) return;
    try {
      await api.delete(`/commons/posts/${postId}/comments/${c.id}`);
      const next = comments.map(x => x.id === c.id ? { ...x, deleted: true, body: "" } : x);
      setComments(next);
      onCountChange?.(liveCount(next));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not delete");
    }
  }

  // Group into tiers for rendering.
  const topLevel = comments.filter(c => !c.parent_comment_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_comment_id) {
      (acc[c.parent_comment_id] = acc[c.parent_comment_id] || []).push(c);
    }
    return acc;
  }, {});

  return (
    <div className="mt-4 pt-4 border-t border-[rgba(179,137,90,0.16)]" data-testid={`post-comments-${postId}`}>
      {loading ? (
        <p className="font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[var(--cream-mute)]">
          loading comments…
        </p>
      ) : topLevel.length === 0 ? (
        <p className="font-garamond italic text-sm text-[var(--cream-dim)]" data-testid={`post-comments-empty-${postId}`}>
          be the first to say something.
        </p>
      ) : (
        <ul className="space-y-4">
          {topLevel.map(c => (
            <CommentRow
              key={c.id}
              comment={c}
              replies={repliesByParent[c.id] || []}
              postOwnerId={postOwnerId}
              currentUserId={currentUserId}
              onReply={() => { setReplyingTo(c.id); setTimeout(() => taRef.current?.focus(), 60); }}
              onDelete={remove}
            />
          ))}
        </ul>
      )}

      {/* Composer */}
      <div className="mt-4" data-testid={`post-comment-composer-${postId}`}>
        {replyingTo && (
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[var(--cream-mute)]">
              replying to a comment
            </span>
            <button
              type="button"
              onClick={() => setReplyingTo(null)}
              data-testid={`post-cancel-reply-${postId}`}
              className="font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[var(--brass)] hover:text-[var(--cream)]"
            >
              cancel
            </button>
          </div>
        )}
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={replyingTo ? "write a reply…" : "add a comment…"}
          rows={2}
          data-testid={`post-comment-input-${postId}`}
          className="w-full bg-[var(--bg)] border border-[rgba(179,137,90,0.24)] rounded p-2.5 font-garamond italic text-sm text-[var(--cream)] placeholder:text-[var(--cream-mute)] focus:outline-none focus:border-[var(--brass)] resize-none"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)]">
            {MAX - text.length} left
          </span>
          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || posting}
            data-testid={`post-comment-send-${postId}`}
            className="lc-btn text-xs"
          >
            {posting ? "posting…" : "post"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentRow({ comment, replies, postOwnerId, currentUserId, onReply, onDelete }) {
  const canDelete = currentUserId && (currentUserId === comment.user_id || currentUserId === postOwnerId);
  return (
    <li data-testid={`comment-${comment.id}`}>
      <CommentBubble comment={comment} canDelete={canDelete && !comment.deleted} onDelete={() => onDelete(comment)} />
      {/* Reply CTA on top-level comments only (one-level cap). */}
      {!comment.deleted && (
        <button
          type="button"
          onClick={onReply}
          data-testid={`comment-reply-${comment.id}`}
          className="ml-11 mt-1 font-mono-lc text-[9px] tracking-[0.16em] uppercase text-[var(--brass)] hover:text-[var(--cream)]"
        >
          reply
        </button>
      )}
      {replies.length > 0 && (
        <ul className="ml-10 mt-2 space-y-3 border-l border-[rgba(179,137,90,0.14)] pl-4">
          {replies.map(r => {
            const can = currentUserId && (currentUserId === r.user_id || currentUserId === postOwnerId);
            return (
              <li key={r.id} data-testid={`comment-${r.id}`}>
                <CommentBubble comment={r} canDelete={can && !r.deleted} onDelete={() => onDelete(r)} />
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function CommentBubble({ comment, canDelete, onDelete }) {
  if (comment.deleted) {
    return (
      <div className="flex gap-3" data-testid={`comment-deleted-${comment.id}`}>
        <div className="w-8 h-8 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.18)] shrink-0" />
        <p className="pt-1 font-garamond italic text-xs text-[var(--cream-mute)]">[comment deleted]</p>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <Link to={comment.author?.username ? `/u/${comment.author.username}` : "#"} className="shrink-0">
        {comment.author?.picture ? (
          <img src={comment.author.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-[rgba(179,137,90,0.24)]" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.24)] flex items-center justify-center font-serif-lit text-xs text-[var(--brass)]">
            {(comment.author?.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <Link
            to={comment.author?.username ? `/u/${comment.author.username}` : "#"}
            className="font-serif-lit text-[var(--cream)] text-sm hover:text-[var(--brass)]"
            data-testid={`comment-author-${comment.id}`}
          >
            {comment.author?.name || "friend"}
          </Link>
          <span className="font-mono-lc text-[9px] tracking-[0.14em] text-[var(--cream-mute)]">
            {formatRelative(comment.created_at)}
          </span>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="delete comment"
              data-testid={`comment-delete-${comment.id}`}
              className="ml-auto text-[var(--cream-mute)] hover:text-[#C86A5C]"
            >
              <Trash2 size={11} strokeWidth={1.6} />
            </button>
          )}
        </div>
        <p className="mt-0.5 font-garamond italic text-sm text-[var(--cream-dim)] whitespace-pre-wrap break-words" data-testid={`comment-body-${comment.id}`}>
          {comment.body}
        </p>
      </div>
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso).getTime();
    const diff = Date.now() - d;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
    return `${Math.floor(diff / 86_400_000)}d`;
  } catch { return ""; }
}
